import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactHtmlParser from "react-html-parser";

import {
  ENTERPRISE_CONTACT_EMAIL,
  PRICING_PAGE_URL,
  SUPPORT_EMAIL,
  TERMS_OF_USE_URL,
} from "../constants";
import { previewUpgrade } from "../helpers/fetch-subscription";
import {
  ADDITIONAL_TOOL_PRICE_EUR,
  BillingAddress,
  Plan,
  PlanKey,
} from "../types/entities";
import {
  BillingAddressDraft,
  BillingAddressForm,
} from "./billing-address-form";
import { ModalLegalFooter } from "./modal-legal-footer";

/** Debounce for the live proration-preview fetch triggered by selecting an
 * upgrade target, matching the project's existing debounce convention (see
 * `RegistrationPage`'s account-name-availability check). */
const PRORATION_PREVIEW_DEBOUNCE_MS = 250;

interface ProrationPreviewState {
  planKey: PlanKey;
  isLoading: boolean;
  amountDueEur?: number;
  currency?: string;
  /** True when the backend could not compute a preview (network error,
   * guard rejection, etc.) — per the UX spec, this must fall back to
   * generic copy rather than a guessed amount, never surface as a hard
   * error. */
  unavailable?: boolean;
}

export interface ChangePlanModalState {
  isOpen: boolean;
  plans: Plan[];
  currentPlan?: Plan | null;
  billingAddress?: BillingAddress | null;
  isSubmitting?: boolean;
  errorMessage?: string;
  /** Set when `errorMessage` was raised by the backend's `DOWNGRADE_BLOCKED`
   * guard (`assertUpgradeOnly`, see `subscription-errors.constants.ts`) —
   * e.g. if an Owner manages to submit a downgrade despite the disabled
   * radio (defense-in-depth). Distinguishes this expected guardrail from a
   * real failure: rendered as an `Alert severity="info"` with the same
   * "contact support" mailto link as the per-plan caption below, never as a
   * generic `severity="error"`. Set by the caller from the API response's
   * `errorCode`, never by matching `errorMessage` text (#2796). */
  downgradeBlocked?: boolean;
  /** Set when `errorMessage` was raised by the backend's
   * `UPGRADE_BLOCKED_CANCELLATION_SCHEDULED` guard (`assertUpgradeOnly`,
   * see `subscription-errors.constants.ts`) — e.g. a stale page that
   * hasn't yet reflected the disabled "Change Plan" button for a
   * `cancel_scheduled` subscription (defense-in-depth). Distinguishes this
   * expected guardrail from a real failure: rendered as an
   * `Alert severity="info"` with the same "contact support" mailto
   * convention as `downgradeBlocked`, never as a generic
   * `severity="error"`. Set by the caller from the API response's
   * `errorCode`, never by matching `errorMessage` text (#2785). */
  cancellationScheduledBlocked?: boolean;
  language: SupportedLanguage;
  /** Needed to call `GET /subscriptions/upgrade-preview` (#2787) directly
   * from this component — see the proration-preview debounce below, which
   * mirrors `RegistrationPage`'s existing debounced-fetch-from-component
   * convention rather than round-tripping through a plugin event, since
   * the preview is purely transient UI state with nothing else in the app
   * depending on it. */
  clientId?: string;
}

export type ChangePlanModalEvent =
  "close-change-plan-modal" | "submit-change-plan";

export interface SubmitChangePlanPayload {
  planKey: PlanKey;
  billingAddress: BillingAddressDraft;
}

export const ChangePlanModal: PimoReactComponent<
  ChangePlanModalState,
  ChangePlanModalEvent,
  SubmitChangePlanPayload
> = ({
  fireEvent,
  isOpen,
  plans,
  currentPlan,
  billingAddress,
  isSubmitting,
  errorMessage,
  downgradeBlocked,
  cancellationScheduledBlocked,
  language,
  clientId,
}) => {
  const [selected, setSelected] = useState<PlanKey | undefined>(
    currentPlan?.key
  );
  const [agreed, setAgreed] = useState(false);
  const [billingValid, setBillingValid] = useState(false);
  const [billingDraft, setBillingDraft] = useState<BillingAddressDraft | null>(
    null
  );
  const [preview, setPreview] = useState<ProrationPreviewState | null>(null);
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewRequestRef = useRef(0);

  const cancelProrationPreview = useCallback(() => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    previewRequestRef.current += 1;
  }, []);

  // Never let a stale response (from a plan the Owner has since clicked
  // away from) overwrite a newer one — same `requestId` guard as
  // `RegistrationPage`'s account-name-availability debounce.
  const scheduleProrationPreview = useCallback(
    (planKey: PlanKey) => {
      cancelProrationPreview();
      const requestId = previewRequestRef.current;
      setPreview({ planKey, isLoading: true });

      previewTimeoutRef.current = setTimeout(async () => {
        if (!clientId) {
          if (previewRequestRef.current === requestId) {
            setPreview({ planKey, isLoading: false, unavailable: true });
          }
          return;
        }
        const result = await previewUpgrade(clientId, planKey);
        if (previewRequestRef.current !== requestId) {
          return;
        }
        if (result.success && result.preview) {
          setPreview({
            planKey,
            isLoading: false,
            amountDueEur: result.preview.amountDueEur,
            currency: result.preview.currency,
          });
        } else {
          setPreview({ planKey, isLoading: false, unavailable: true });
        }
      }, PRORATION_PREVIEW_DEBOUNCE_MS);
    },
    [cancelProrationPreview, clientId]
  );

  // Cancel any in-flight debounce/preview on unmount, so a dialog closed
  // mid-fetch never updates state after it is gone.
  useEffect(() => cancelProrationPreview, [cancelProrationPreview]);

  const planHasChanged = !!selected && selected !== currentPlan?.key;
  const isUpgrade = (plan: Plan) =>
    !currentPlan || plan.basePriceEur > currentPlan.basePriceEur;

  const addressHasChanged =
    billingDraft != null &&
    (!billingAddress ||
      billingDraft.companyName !== billingAddress.companyName ||
      billingDraft.vatId !== (billingAddress.vatId ?? "") ||
      billingDraft.line1 !== billingAddress.line1 ||
      billingDraft.line2 !== (billingAddress.line2 ?? "") ||
      billingDraft.postalCode !== billingAddress.postalCode ||
      billingDraft.city !== billingAddress.city ||
      billingDraft.country !== billingAddress.country);

  // A plan change must be an upgrade and confirmed; a billing-only change needs
  // neither (the plan stays the same, so there is nothing to confirm).
  const canSubmit =
    !!selected &&
    billingValid &&
    !isSubmitting &&
    billingDraft != null &&
    (planHasChanged || addressHasChanged) &&
    (!planHasChanged ||
      (agreed &&
        isUpgrade(
          plans.find((p) => p.key === selected) ?? ({ basePriceEur: 0 } as Plan)
        )));

  const submitLabel = getLocalizedMessage(
    language,
    `dialogActions.${!planHasChanged && addressHasChanged ? "save" : "upgradePlan"}`
  );

  const selectedPlan = plans.find((p) => p.key === selected);
  const showProrationPreview =
    planHasChanged && !!selectedPlan && isUpgrade(selectedPlan);
  const previewMatchesSelection = preview?.planKey === selected;

  const handleSelectPlan = (planKey: PlanKey) => {
    setSelected(planKey);
    const plan = plans.find((p) => p.key === planKey);
    if (
      plan &&
      currentPlan &&
      plan.key !== currentPlan.key &&
      isUpgrade(plan)
    ) {
      scheduleProrationPreview(planKey);
    } else {
      cancelProrationPreview();
      setPreview(null);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => fireEvent?.("close-change-plan-modal")}
      slotProps={{
        transition: {
          onEnter: () => {
            setSelected(currentPlan?.key);
            setAgreed(false);
            cancelProrationPreview();
            setPreview(null);
          },
        },
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {getLocalizedMessage(language, "changePlanModal.title")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {currentPlan ? (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
              >
                {getLocalizedMessage(language, "changePlanModal.currentPlan")}
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: "#dff5e1",
                  color: "#2e7d32",
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                  fontWeight: 600,
                }}
              >
                <CheckCircleIcon fontSize="small" />
                {getLocalizedMessage(
                  language,
                  "changePlanModal.currentPlanChip",
                  {
                    plan: currentPlan.name,
                    userLimit: currentPlan.userLimit,
                  }
                )}
              </Box>
            </Box>
          ) : null}

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {getLocalizedMessage(language, "changePlanModal.selectNewPlan")}
          </Typography>

          <RadioGroup
            value={selected ?? ""}
            onChange={(e) => handleSelectPlan(e.target.value as PlanKey)}
          >
            <Stack spacing={1}>
              {plans.map((plan) => {
                const isCurrent = plan.key === currentPlan?.key;
                const downgrade = !isCurrent && !isUpgrade(plan);
                const disabled = isCurrent || downgrade;
                const caption = isCurrent
                  ? getLocalizedMessage(language, "changePlanModal.currentPlan")
                  : getLocalizedMessage(
                      language,
                      "changePlanModal.upToXUsers",
                      { userLimit: plan.userLimit }
                    );
                return (
                  <Box
                    key={plan.key}
                    sx={{
                      border: "1px solid",
                      borderColor:
                        selected === plan.key ? "primary.main" : "#e0e0e0",
                      borderRadius: 2,
                      p: 2,
                      opacity: disabled ? 0.45 : 1,
                    }}
                  >
                    <FormControlLabel
                      value={plan.key}
                      disabled={disabled}
                      control={<Radio size="small" />}
                      sx={{ m: 0, width: "100%" }}
                      label={
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                          >
                            {plan.name}
                          </Typography>
                          {downgrade ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              data-testid="downgrade-not-available"
                            >
                              {getLocalizedMessage(
                                language,
                                "changePlanModal.downgradeNotAvailable"
                              )}{" "}
                              <Link
                                href={`mailto:${SUPPORT_EMAIL}?subject=Downgrade%20request`}
                                underline="hover"
                              >
                                {getLocalizedMessage(
                                  language,
                                  "changePlanModal.downgradeContactSupport"
                                )}
                              </Link>
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {isCurrent
                                ? caption
                                : getLocalizedMessage(
                                    language,
                                    "changePlanModal.currentPlanDescription",
                                    {
                                      price: plan.basePriceEur,
                                      userLimit: plan.userLimit,
                                    }
                                  )}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                );
              })}
            </Stack>
          </RadioGroup>

          {/* Enterprise – contact sales */}
          <Box
            sx={{
              border: "1px dashed #bdbdbd",
              borderRadius: 2,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "#f5f5f5",
              opacity: 0.75,
            }}
          >
            <Radio size="small" disabled sx={{ p: 0.5 }} />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: "text.disabled",
                }}
              >
                {getLocalizedMessage(language, "changePlanModal.enterprise")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getLocalizedMessage(
                  language,
                  "changePlanModal.availableOnRequest"
                )}
                <Link
                  href={`mailto:${ENTERPRISE_CONTACT_EMAIL}`}
                  underline="hover"
                >
                  {getLocalizedMessage(language, "changePlanModal.contactUs")}
                </Link>
              </Typography>
            </Box>
          </Box>

          {/* Tool pricing info */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              p: 1.5,
              bgcolor: "#fafafa",
            }}
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ color: "primary.main", mt: 0.1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {ReactHtmlParser(
                getLocalizedMessage(
                  language,
                  "changePlanModal.activateOneFree",
                  {
                    additionalAppPrice: ADDITIONAL_TOOL_PRICE_EUR,
                  }
                )
              )}
            </Typography>
          </Box>

          {showProrationPreview ? (
            <Box
              data-testid="proration-preview"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 1.5,
                bgcolor: "#fafafa",
              }}
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "primary.main", mt: 0.1 }}
              />
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {getLocalizedMessage(
                    language,
                    "changePlanModal.prorationPreview.newPrice",
                    { price: selectedPlan?.basePriceEur ?? 0 }
                  )}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0.5 }}
                >
                  {previewMatchesSelection && preview?.isLoading ? (
                    <CircularProgress size={14} />
                  ) : null}
                  <Typography variant="body2" color="text.secondary">
                    {previewMatchesSelection && preview?.isLoading
                      ? getLocalizedMessage(
                          language,
                          "changePlanModal.prorationPreview.loading"
                        )
                      : previewMatchesSelection &&
                          !preview?.unavailable &&
                          preview?.amountDueEur !== undefined
                        ? getLocalizedMessage(
                            language,
                            "changePlanModal.prorationPreview.amount",
                            {
                              amount: preview.amountDueEur.toFixed(2),
                              currency: (
                                preview.currency ?? "EUR"
                              ).toUpperCase(),
                            }
                          )
                        : getLocalizedMessage(
                            language,
                            "changePlanModal.prorationPreview.fallback"
                          )}
                  </Typography>
                </Stack>
              </Box>
            </Box>
          ) : null}

          <Alert severity="info">
            {getLocalizedMessage(
              language,
              "changePlanModal.upgradeExplanation"
            )}
          </Alert>

          <Typography variant="body2">
            <Link
              href={PRICING_PAGE_URL}
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{ color: "primary.main", fontWeight: 500 }}
            >
              {getLocalizedMessage(
                language,
                "changePlanModal.fullPricingDetails"
              )}
            </Link>
          </Typography>

          <BillingAddressForm
            address={billingAddress}
            onChange={setBillingDraft}
            onValidityChange={setBillingValid}
            language={language}
          />

          {planHasChanged ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  {getLocalizedMessage(
                    language,
                    "changePlanModal.upgradeConfirmation"
                  )}{" "}
                  <Link href={TERMS_OF_USE_URL} target="_blank" rel="noopener">
                    {getLocalizedMessage(
                      language,
                      "changePlanModal.termsOfUse"
                    )}
                  </Link>
                  .
                </Typography>
              }
            />
          ) : null}

          {errorMessage ? (
            downgradeBlocked ? (
              // Per the #2785 UX spec (flow c): a blocked downgrade is an
              // expected guardrail, not a system failure, so it is surfaced
              // as `severity="info"` with the same mailto convention as the
              // per-plan caption above — never a dead-end generic error.
              <Alert severity="info" data-testid="downgrade-blocked-alert">
                {errorMessage}{" "}
                <Link
                  href={`mailto:${SUPPORT_EMAIL}?subject=Downgrade%20request`}
                  underline="hover"
                >
                  {getLocalizedMessage(
                    language,
                    "changePlanModal.downgradeContactSupport"
                  )}
                </Link>
              </Alert>
            ) : cancellationScheduledBlocked ? (
              // Same treatment as `downgradeBlocked` above: an upgrade
              // blocked because the subscription is already
              // `cancel_scheduled` is also an expected guardrail (not a
              // system failure) — the Owner already asked to cancel, and
              // the "Change Plan" button should normally be disabled
              // before this is ever reached (defense-in-depth, #2785).
              <Alert
                severity="info"
                data-testid="cancellation-scheduled-blocked-alert"
              >
                {errorMessage}{" "}
                <Link
                  href={`mailto:${SUPPORT_EMAIL}?subject=Plan%20change%20request`}
                  underline="hover"
                >
                  {getLocalizedMessage(
                    language,
                    "changePlanModal.downgradeContactSupport"
                  )}
                </Link>
              </Alert>
            ) : (
              <Alert severity="error">{errorMessage}</Alert>
            )
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 1 }}>
        <Button onClick={() => fireEvent?.("close-change-plan-modal")}>
          {getLocalizedMessage(language, "dialogActions.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => {
            if (!canSubmit || !billingDraft || !selected) return;
            fireEvent?.("submit-change-plan", {
              planKey: selected,
              billingAddress: billingDraft,
            });
          }}
          sx={{
            bgcolor: "#ff8c00",
            color: "#fff",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#e67e00" },
            "&.Mui-disabled": { bgcolor: "#f0d4b3", color: "#fff" },
          }}
        >
          {submitLabel}
        </Button>
      </DialogActions>
      <ModalLegalFooter language={language} />
    </Dialog>
  );
};
